import { AnimatePresence, TargetAndTransition, motion } from 'framer-motion';
import {
	type ComponentProps,
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { tokens } from '../theme/tokens';
import { IconButton } from './button/IconButton';
import { Card } from './Card';
import { Popover } from './Popover';
import { Spinner } from './Spinner';
import { Heading3 } from './Text';

export const ContextMenuContext = createContext({
	dismiss: () => {},
});

const ItemContainer = styled(Flex)<{
	type?: 'primary' | 'destructive';
	disabled?: boolean;
	$alignStart?: boolean;
	$highlighted?: boolean;
}>`
	align-items: ${(props) => (props.$alignStart ? 'flex-start' : 'center')};
	justify-content: space-between;
	width: 100%;
	box-sizing: border-box;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.compact}px;
	color: ${(props) => (props.type === 'destructive' ? tokens.error.text : tokens.text.secondary)};
	font-size: ${tokens.typography.sizes.label}px;
	font-weight: ${tokens.typography.weights.medium};
	font-family: ${tokens.typography.fontFamily};
	line-height: 20px;
	border-radius: ${tokens.radius.sm}px;
	cursor: pointer;
	opacity: ${(props) => (props.disabled ? 0.5 : 1)};
	${(props) => props.disabled && `pointer-events: none;`}
	filter: ${(props) => (props.disabled ? 'grayscale(100%)' : 'none')};
	${(props) => props.$highlighted && `background: ${tokens.surface.secondary};`}

	&:hover,
	&:active {
		background: ${tokens.surface.secondary};
	}
`;

const ItemIcon = styled(Box)<{ $destructive?: boolean; $hasSubtitle?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${(props) => (props.$hasSubtitle ? 30 : 20)}px;
	height: ${(props) => (props.$hasSubtitle ? 30 : 20)}px;

	svg {
		display: block;
		width: 100%;
		height: 100%;
		color: ${(props) => (props.$destructive ? tokens.error.icon : tokens.icon.secondary)};
	}
`;

const ItemChevron = styled(Box)`
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;

	svg {
		display: block;
		width: 16px;
		height: 16px;
		color: ${tokens.icon.secondary};
	}
`;

const Menu = styled.div<{ $width?: number; $minWidth?: number }>`
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	padding: ${tokens.padding.compact}px;
	width: ${(props) => (props.$width != null ? `${props.$width}px` : 'auto')};
	min-width: ${(props) => (props.$minWidth != null ? `${props.$minWidth}px` : undefined)};
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export function ContextMenu(props: PropsWithChildren<{ width?: number; minWidth?: number }>) {
	return (
		<Menu role="menu" $width={props.width} $minWidth={props.minWidth}>
			{props.children}
		</Menu>
	);
}

const ContextMenuSeparator = styled.div`
	margin: ${tokens.gap.element}px 0;
	background: ${tokens.border.subtle};
	height: 1px;
	width: 100%;
	flex-shrink: 0;
`;

const CloseButton = styled(IconButton)`
	&:hover {
		svg {
			transform: translateX(-2px);
		}

		&:active {
			svg {
				transform: translateX(-2px);
			}
		}
	}
`;

ContextMenu.Separator = ContextMenuSeparator;
ContextMenu.Group = Group;

type ContextMenuItemProps = {
	icon?: ReactNode;
	title?: string | ReactNode;
	description?: string | ReactNode | undefined;
	type?: 'primary' | 'destructive';
	hasSubtitle?: boolean | undefined;
	disabled?: boolean | undefined;
	content?: ReactNode | undefined;
	onClick?: () => Promise<unknown> | void;
	padding?: number;
	link?: boolean;
	preventCloseOnClick?: boolean;
	/** Keeps the hover background while a submenu of this item is open. */
	highlighted?: boolean;
	className?: string;
};

function ContextMenuItem(props: ContextMenuItemProps) {
	const [loading, setLoading] = useState(false);
	const context = useContext(ContextMenuContext);

	const handleOnClick = async () => {
		if (props.disabled) {
			return;
		}

		setLoading(true);
		await props.onClick?.();
		setLoading(false);
		if (!props.preventCloseOnClick) {
			context.dismiss();
		}
	};

	const alignStart = Boolean(props.description);

	return (
		<ItemContainer
			role="menuitem"
			aria-label={typeof props.title === 'string' ? props.title : undefined}
			className={props.className}
			$alignStart={alignStart}
			$highlighted={props.highlighted}
			onClick={() => handleOnClick()}
			type={props.type}
			disabled={props.disabled}
			display="flex"
		>
			<Flex
				alignItems={alignStart ? 'flex-start' : 'center'}
				style={{ gap: tokens.gap.element, minWidth: 0 }}
			>
				{(props.icon || loading) && !props.content && (
					<ItemIcon $destructive={props.type === 'destructive'} $hasSubtitle={props.hasSubtitle}>
						{loading ? (
							<Flex justifyContent="center">
								<Spinner color={props.type === 'destructive' ? 'red' : undefined} />
							</Flex>
						) : (
							props.icon
						)}
					</ItemIcon>
				)}

				{(props.title || props.description) && (
					<Flex
						height="100%"
						flexDirection="column"
						textAlign="start"
						minWidth={0}
						overflow="hidden"
					>
						<Box style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{props.title}
						</Box>
						{props.description}
					</Flex>
				)}
			</Flex>

			{props.link && (
				<ItemChevron>
					<ChevronRightIcon />
				</ItemChevron>
			)}

			{props.content}
		</ItemContainer>
	);
}

ContextMenu.Item = ContextMenuItem;

type ContextMenuPopoverProps = Omit<
	ComponentProps<typeof Popover.Target>,
	'visible' | 'onDismiss' | 'contents'
>;

type ContextMenuTargetProps = PropsWithChildren<
	{
		menu: ReactNode;
		disabled?: boolean;
		closeMenu?: boolean;
	} & ContextMenuPopoverProps
>;

function ContextMenuTarget({
	children,
	menu,
	disabled,
	closeMenu,
	...popoverProps
}: ContextMenuTargetProps) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (closeMenu) {
			setVisible(false);
		}
	}, [closeMenu]);

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<ContextMenuContext.Provider value={{ dismiss: () => setVisible(false) }}>
					{menu}
				</ContextMenuContext.Provider>
			}
			{...popoverProps}
		>
			<Box
				onClick={(event) => {
					if (!disabled) {
						setVisible(true);
						event.stopPropagation();
						event.preventDefault();
					}
				}}
			>
				{children}
			</Box>
		</Popover.Target>
	);
}

ContextMenu.Target = ContextMenuTarget;

type ContextMenuCardTargetProps = Omit<
	ContextMenuTargetProps,
	'backdrop' | 'menu' | 'disabled' | 'closeMenu'
>;

type ContextMenuCardProps = PropsWithChildren<
	BoxProps & {
		title?: string;
		hasBackButton: boolean;
		trigger: ReactNode;
		footer?: ReactNode;
		disabled?: boolean;
		closeMenu?: boolean;
		view?: number;
		items?: ContextMenuItemProps[];
		onViewChange?: (view: number) => void;
		targetProps?: ContextMenuCardTargetProps;
	}
>;

const ContextMenuCardViewVariants: {
	initial: (direction: number) => TargetAndTransition;
	animate: TargetAndTransition;
	exit: (direction: number) => TargetAndTransition;
} = {
	initial: (direction: number) => ({
		x: direction > 0 ? 100 : -100,
		opacity: 0,
		position: 'absolute',
	}),
	animate: {
		x: 0,
		opacity: 1,
		position: 'relative',
		transition: { type: 'spring', stiffness: 500, damping: 40, duration: 4 },
	},
	exit: (direction: number) => ({
		x: direction < 0 ? 100 : -100,
		opacity: 0,
		position: 'absolute',
		transition: { duration: 0.18 },
	}),
};

function ContextMenuCard({
	title,
	hasBackButton,
	trigger,
	footer,
	children,
	disabled,
	closeMenu,
	view,
	items,
	onViewChange,
	targetProps,
	...rest
}: ContextMenuCardProps) {
	// Determines animation direction for view transitions.
	const direction = view && view > 0 ? 1 : -1;

	const card = (
		<Card {...rest} p={0}>
			{(title || hasBackButton) && (
				<>
					<Flex alignItems="center" px={hasBackButton ? 1 : 3} py={hasBackButton ? '8px' : '12px'}>
						{hasBackButton && onViewChange && (
							<CloseButton
								type="plain"
								icon={<ChevronLeftIcon />}
								onClick={() => onViewChange(0)}
							/>
						)}
						{title && <Heading3>{title}</Heading3>}
					</Flex>

					<ContextMenuSeparator />
				</>
			)}
			{view !== undefined ? (
				<Box minWidth={200} minHeight={120}>
					<AnimatePresence custom={direction} initial={false} mode="wait">
						<motion.div
							key={view}
							variants={ContextMenuCardViewVariants}
							initial="initial"
							animate="animate"
							exit="exit"
							custom={direction}
							style={{ width: '100%' }}
						>
							{view === 0 && items && items.length > 0 && (
								<ContextMenu>
									{items.map((item, idx) => (
										<ContextMenu.Item
											key={idx}
											icon={item.icon}
											title={item.title}
											description={item.description}
											onClick={item.onClick}
											disabled={item.disabled}
											link={item.link}
											preventCloseOnClick={item.preventCloseOnClick}
										/>
									))}
								</ContextMenu>
							)}

							{view !== 0 && <>{children}</>}
						</motion.div>
					</AnimatePresence>
				</Box>
			) : (
				<Box>{children}</Box>
			)}

			{footer && (
				<Box>
					<ContextMenuSeparator />
					{footer}
				</Box>
			)}
		</Card>
	);

	return (
		<ContextMenu.Target
			backdrop
			menu={card}
			disabled={disabled}
			closeMenu={closeMenu}
			{...targetProps}
		>
			{trigger}
		</ContextMenu.Target>
	);
}

ContextMenu.Card = ContextMenuCard;
